import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Zap, Globe, UserCog, UserPlus, Search, PhoneCall, CalendarCheck, Lock, Loader2, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { FlexSearch } from "../components/FlexSearch";

const CONFIG = {
  onsite: {
    icon: Zap,
    eyebrow: "On-site · shift · gig",
    title: "Darriker Flex",
    tagline: "Hire on-site, part-time & shift workers on demand — warehouse, cleaning, forklift, labor, retail & more.",
    sampleCats: ["Warehouse", "Inventory", "Cleaning", "Forklift Driver", "General Labor", "Delivery & Driving", "Retail", "Care & Support"],
    otherLink: { to: "/marketplace/remote", label: "Looking for remote workers? Visit Darriker Remote →" },
  },
  remote: {
    icon: Globe,
    eyebrow: "Remote · part-time · contract",
    title: "Darriker Remote",
    tagline: "Find remote part-time & contract talent — data entry, virtual assistants, support, bookkeeping, design & more.",
    sampleCats: ["Data Entry", "Virtual Assistant", "Customer Support", "Bookkeeping", "Content & Writing", "Design", "Dev & IT"],
    otherLink: { to: "/marketplace/onsite", label: "Need on-site help instead? Visit Darriker Flex →" },
  },
};

const WORKER_STEPS = [
  { icon: UserPlus, title: "Create your listing", desc: "Pick your categories, wage and skills in minutes — free to list." },
  { icon: CalendarCheck, title: "Set availability", desc: "Mark recurring days and block off dates you're unavailable." },
  { icon: PhoneCall, title: "Get contacted", desc: "Employers find you by match score and call you directly." },
];
const EMPLOYER_STEPS = [
  { icon: Search, title: "Search & filter", desc: "Filter by category, availability, wage and location." },
  { icon: Zap, title: "See match scores", desc: "Workers ranked by how well they fit your needs." },
  { icon: PhoneCall, title: "Call instantly", desc: "One tap to phone the right worker — no middleman." },
];

export default function Marketplace({ mode }) {
  const { user } = useAuth();
  const cfg = CONFIG[mode];
  const Icon = cfg.icon;
  const isEmployer = user?.role === "employer";
  const entitled = ["employer_pro", "flex_pro"].includes(user?.plan);

  return (
    <div data-testid={`marketplace-${mode}`}>
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <div className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#8AA2FF] flex items-center gap-2">
            <Icon size={15} /> {cfg.eyebrow}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">{cfg.title}</h1>
          <p className="text-base text-[#C7CBD1] mt-4 max-w-2xl">{cfg.tagline}</p>
          {!user && (
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/auth?mode=register" data-testid="mkt-cta-worker" className="bg-white text-[#0A0A0A] px-6 py-3 text-sm font-medium hover:bg-[#E5E7EB] transition-colors">List your availability — free</Link>
              <Link to="/auth?mode=register" data-testid="mkt-cta-employer" className="border border-white px-6 py-3 text-sm font-medium hover:bg-white hover:text-[#0A0A0A] transition-colors">Hire flexible talent</Link>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-7">
            {cfg.sampleCats.map((c) => (
              <span key={c} className="font-mono2 text-[10px] uppercase border border-white/25 text-[#C7CBD1] px-2.5 py-1">{c}</span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 py-10">
        {isEmployer ? (
          entitled ? <FlexSearch mode={mode} /> : <FlexPaywall cfg={cfg} />
        ) : (
          <>
            {/* HOW IT WORKS */}
            <div className="grid md:grid-cols-2 gap-8">
              <StepColumn label="For workers" steps={WORKER_STEPS} />
              <StepColumn label="For employers" steps={EMPLOYER_STEPS} />
            </div>

            {/* role-based CTA */}
            <div className="mt-10 border border-[#0A0A0A] p-8 text-center space-y-3">
              {user?.role === "flex" ? (
                <div data-testid="marketplace-flex-cta" className="space-y-3">
                  <UserCog size={28} className="text-[#002FA7] mx-auto" />
                  <h2 className="font-display font-bold text-xl">This is where employers find you</h2>
                  <p className="text-sm text-[#4B5563] max-w-md mx-auto">Complete your availability, categories and wage so you show up in {cfg.title} searches.</p>
                  <Link to="/flex" data-testid="marketplace-manage-listing" className="btn-ink inline-block px-6 py-2.5 text-sm font-medium">Manage my listing</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <h2 className="font-display font-bold text-xl">Hire flexible talent, fast</h2>
                  <p className="text-sm text-[#4B5563]">Sign in as an employer to search and contact workers directly.</p>
                  <Link to="/auth" className="btn-ink inline-block px-6 py-2.5 text-sm font-medium">Sign in to browse</Link>
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <Link to={cfg.otherLink.to} className="font-mono2 text-xs text-[#002FA7] hover:underline">{cfg.otherLink.label}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FlexPaywall({ cfg }) {
  const [loading, setLoading] = useState(null);
  const subscribe = async (pkg) => {
    setLoading(pkg);
    try {
      const r = await api.post("/payments/checkout", { package_id: pkg, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(null);
    }
  };
  return (
    <div className="border-2 border-dashed border-[#0A0A0A] p-8" data-testid="flex-paywall">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} className="text-[#002FA7]" />
        <h2 className="font-display font-bold text-xl">Subscribe to search {cfg.title}</h2>
      </div>
      <p className="text-sm text-[#4B5563] mb-6 max-w-lg">Unlock worker availability, wages and direct contact. Cancel anytime.</p>
      <div className="grid sm:grid-cols-2 gap-px bg-[#0A0A0A] border border-[#0A0A0A] max-w-2xl">
        <div className="bg-white p-5 flex flex-col">
          <div className="font-mono2 text-xs uppercase text-[#10B981]">Marketplace only</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1">Flex Marketplace</div>
          <div className="font-display font-black text-4xl tracking-tighter text-[#002FA7] mt-2">$99<span className="text-base text-[#4B5563]">/mo</span></div>
          <ul className="mt-3 space-y-1 text-sm flex-1">
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Search Flex + Remote workers</li>
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Filters, match scores</li>
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Direct click-to-call</li>
          </ul>
          <button data-testid="flex-subscribe-btn" onClick={() => subscribe("flex_pro")} disabled={!!loading} className="btn-ink w-full py-2.5 text-sm font-medium mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {loading === "flex_pro" ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} Get Flex — $99/mo
          </button>
        </div>
        <div className="bg-white p-5 flex flex-col">
          <div className="font-mono2 text-xs uppercase text-[#4B5563]">Full recruiter suite</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1">Employer Pro</div>
          <div className="font-display font-black text-4xl tracking-tighter text-[#002FA7] mt-2">$299<span className="text-base text-[#4B5563]">/mo</span></div>
          <ul className="mt-3 space-y-1 text-sm flex-1">
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Everything in Flex</li>
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Unlimited job posts + AI copilot</li>
            <li className="flex gap-2"><Check size={15} className="text-[#10B981] shrink-0" />Candidate shortlists & screening</li>
          </ul>
          <button data-testid="pro-subscribe-btn" onClick={() => subscribe("employer_pro")} disabled={!!loading} className="btn-outline w-full py-2.5 text-sm font-medium mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {loading === "employer_pro" ? <Loader2 size={16} className="animate-spin" /> : null} Get Employer Pro
          </button>
        </div>
      </div>
    </div>
  );
}

function StepColumn({ label, steps }) {
  return (
    <div>
      <div className="font-mono2 text-xs uppercase tracking-wider text-[#002FA7] mb-4">{label}</div>
      <div className="space-y-5">
        {steps.map((s, i) => {
          const SIcon = s.icon;
          return (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 shrink-0 border border-[#0A0A0A] flex items-center justify-center">
                <SIcon size={18} className="text-[#0A0A0A]" />
              </div>
              <div>
                <div className="font-display font-bold text-base tracking-tight">{s.title}</div>
                <div className="text-sm text-[#4B5563]">{s.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
